import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject, Injectable } from '@angular/core';
import { combineLatest, concat, defer, fromEvent, Observable, of } from 'rxjs';
import { concatMap, filter, map } from 'rxjs/operators';

@Injectable()
export class VisibilityService {
    private pageVisible$: Observable<boolean>;

    public constructor(@Inject(DOCUMENT) document: Document) {
        this.pageVisible$ = concat(
            defer(() => of(!document.hidden)),
            fromEvent(document, 'visibilitychange').pipe(map(() => !document.hidden)),
        );
    }

    public elementInSight(element: ElementRef): Observable<boolean> {
        const elementVisible$ = new Observable<IntersectionObserverEntry[]>((observer) => {
            const intersectionObserver = new IntersectionObserver((entries) => {
                observer.next(entries);
            });

            intersectionObserver.observe(element.nativeElement);

            return () => {
                intersectionObserver.disconnect();
            };
        }).pipe(
            concatMap((entries) => entries),
            map((entry) => entry.isIntersecting),
            filter((isVisible) => isVisible),
        );

        const elementInSight$ = combineLatest([this.pageVisible$, elementVisible$]).pipe(
            map(([pageVisible, elementVisible]) => pageVisible && elementVisible),
            filter((isVisible) => isVisible),
        );

        return elementInSight$;
    }
}
